import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react';
import { Button } from './Button';

/* ────────────────────────────────────── Card.Head */

export interface CardHeadProps {
  /** Mono uppercase title label (left side) */
  title: ReactNode;
  /** Optional count badge (mono, faint) */
  count?: ReactNode;
  /** If true, a flex-grow spacer separates left from right content */
  grow?: boolean;
  /** "View all" link label — renders as a ghost sm button */
  viewAll?: string;
  onViewAll?: ButtonHTMLAttributes<HTMLButtonElement>['onClick'];
  /** Optional icon buttons for the right side */
  actions?: ReactNode;
  className?: string;
}

function CardHead({
  title,
  count,
  grow,
  viewAll,
  onViewAll,
  actions,
  className,
}: CardHeadProps) {
  return (
    <header className={['pn-card-head', className].filter(Boolean).join(' ')}>
      <span className="pn-card-head-title">{title}</span>
      {count != null ? (
        <span className="pn-card-head-count">{count}</span>
      ) : null}
      {grow ? <span className="pn-card-head-grow" /> : null}
      {viewAll ? (
        <Button
          variant="ghost"
          size="sm"
          className="pn-card-head-viewall"
          onClick={onViewAll}
          type="button"
        >
          {viewAll}
          <span className="pn-card-head-viewall-arr" aria-hidden>→</span>
        </Button>
      ) : null}
      {actions ? (
        <span className="pn-card-head-actions">{actions}</span>
      ) : null}
    </header>
  );
}

/* ────────────────────────────────────── Card.Body */

export interface CardBodyProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

function CardBody({ children, className, ...rest }: CardBodyProps) {
  return (
    <div className={['pn-card-body', className].filter(Boolean).join(' ')} {...rest}>
      {children}
    </div>
  );
}

/* ────────────────────────────────────── Card.Foot */

export interface CardFootProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

function CardFoot({ children, className, ...rest }: CardFootProps) {
  return (
    <div className={['pn-card-foot', className].filter(Boolean).join(' ')} {...rest}>
      {children}
    </div>
  );
}

/* ────────────────────────────────────── Card */

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  withCorners?: boolean;
  tag?: ReactNode;
  tagAccent?: boolean;
}

export function Card({
  children,
  className,
  withCorners,
  tag,
  tagAccent,
  ...rest
}: CardProps) {
  return (
    <div className={['pn-card', className].filter(Boolean).join(' ')} {...rest}>
      {tag ? (
        <span className={['pn-card-tag pn-tag', tagAccent ? 'pn-tag-accent' : ''].filter(Boolean).join(' ')}>
          {tag}
        </span>
      ) : null}
      {withCorners ? (
        <>
          <span className="pn-corner pn-corner-tl" />
          <span className="pn-corner pn-corner-tr" />
          <span className="pn-corner pn-corner-bl" />
          <span className="pn-corner pn-corner-br" />
        </>
      ) : null}
      {children}
    </div>
  );
}

Card.Head = CardHead;
Card.Body = CardBody;
Card.Foot = CardFoot;
